<script setup lang="ts">
import { computed } from 'vue'
import ComparisonTable from './ComparisonTable.vue'
import * as da from '@/strings/da'
import { useCopyButton } from '@/composables/useCopyButton'
import { buildNarrative } from '@/lib/calltest/narrative'
import { buildTechnicalDetails } from '@/lib/calltest/redact'
import { VERDICT_TONE } from '@/lib/presentation'
import type { ApiFailure } from '@/lib/calltest/api-failure'
import type { BrowserMeasurement } from '@/lib/calltest/stats'
import type { CallFailure } from '@/lib/calltest/sip'
import type { CallTestResult, Verdict } from '@/lib/calltest/types'

const props = defineProps<{
	apiFailure: ApiFailure | null
	callFailure: CallFailure | null
	connected: boolean
	verdict: Verdict
	worseDirection: 'INBOUND' | 'OUTBOUND' | null
	platform: CallTestResult | null
	platformStillPending: boolean
	browser: BrowserMeasurement | null
	tonePeak: number
	browserMediaMs: number | null
	remoteAudibleMs: number
}>()

const narrative = computed(() => buildNarrative({
	apiFailure: props.apiFailure,
	callFailure: props.callFailure,
	connected: props.connected,
	verdict: props.verdict,
	worseDirection: props.worseDirection,
	platform: props.platform,
	platformStillPending: props.platformStillPending,
	inboundPackets: props.browser?.inboundPackets ?? 0,
	remoteAudibleMs: props.remoteAudibleMs
}))

/** A failure to start has no verdict to colour by; it is simply a failure. */
const tone = computed(() => (props.apiFailure || props.callFailure || !props.connected)
	? 'fail'
	: VERDICT_TONE[props.verdict])

/** Worst first, as the platform ordered them. */
const findings = computed(() => props.platform?.quality?.findings ?? [])

const details = computed(() => buildTechnicalDetails(props.platform, props.browser?.samples ?? []))
const { state: copyState, copy } = useCopyButton(() => details.value)
</script>

<template>
	<section class="result">
		<div class="verdict" :class="`tone-${tone}`">
			<h3 class="headline">{{ narrative.headline }}</h3>
			<p v-for="note in narrative.notes" :key="note" class="note">{{ note }}</p>
		</div>

		<ul v-if="findings.length > 0" class="findings">
			<li v-for="(item, index) in findings" :key="`${item.code}-${index}`">
				<span class="bullet" aria-hidden="true">□</span>
				<span>{{ da.finding[item.code] }}</span>
			</li>
		</ul>

		<ComparisonTable
			:platform="platform?.audio ?? null"
			:browser="browser"
			:tone-peak="tonePeak"
			:browser-media-ms="browserMediaMs"
		/>

		<details class="technical">
			<summary>{{ da.technical.summary }}</summary>
			<p class="lede">{{ da.technical.lede }}</p>
			<button type="button" class="btn btn-tcx-outline btn-sm" @click="copy">
				{{ copyState === 'copied' ? da.technical.copied : da.technical.copy }}
			</button>
			<p v-if="copyState === 'failed'" class="copy-failed">{{ da.technical.copyFailed }}</p>
			<pre class="payload">{{ details }}</pre>
		</details>
	</section>
</template>


<style scoped>
.result {
	display: flex;
	flex-direction: column;
	gap: 26px;
}

.verdict {
	padding: 18px 20px;
	border: 1px solid var(--tone-border);
	border-left: 4px solid var(--tone-fg);
	border-radius: var(--tcx-radius-sm);
	background: var(--tone-bg);
}

.headline {
	font-size: 1.0625rem;
	font-weight: 700;
	color: var(--tone-fg);
	max-width: 62ch;
}

.note {
	margin: 8px 0 0;
	max-width: 62ch;
	font-size: 0.9375rem;
	color: var(--tcx-text);
}

.findings {
	margin: 0;
	padding: 0;
	list-style: none;
}

.findings li {
	display: flex;
	gap: 10px;
	padding: 6px 0;
	max-width: 66ch;
	font-size: 0.9375rem;
}

.bullet {
	color: var(--tcx-teal);
	font-size: 1.125rem;
	line-height: 1.35;
}

.technical summary {
	cursor: pointer;
	font-weight: 500;
	font-size: 0.9375rem;
}

.technical .lede {
	margin: 10px 0;
	max-width: 62ch;
	font-size: 0.875rem;
	color: var(--tcx-muted);
}

.copy-failed {
	margin: 10px 0 6px;
	font-size: 0.875rem;
}

.payload {
	margin: 12px 0 0;
	padding: 12px 14px;
	max-height: 320px;
	overflow: auto;
	border: 1px solid var(--tcx-line);
	border-radius: var(--tcx-radius-sm);
	background: #fafbfb;
	font-size: 0.75rem;
	color: var(--tcx-text);
}
</style>
